/**
 * Pure helpers for the post-login redirect. Kept free of app-level imports so
 * they can be unit-tested without dragging in the Vite-only module graph.
 */
import { APP_ROUTE, ONBOARDING } from '@/constants';
import sortClientsAscendingByDate from '@/helpers/sort-clients-ascending-by-date';

/**
 * Normalises the `callbackUrl` cookie to a same-origin path, or returns
 * `undefined` when it must not be honoured (foreign origin, protocol-relative,
 * or pointing back into `/auth/*`, which would loop).
 */
export function sanitizeCallbackUrl(raw: unknown): string | undefined {
  if (typeof raw !== 'string' || raw.length === 0) return undefined;
  let path = raw;
  const { origin } = window.location;
  if (path.startsWith(`${origin}/`)) path = path.slice(origin.length);
  if (!path.startsWith('/') || path.startsWith('//')) return undefined;
  // Compare the DECODED path so `/%61uth/login` cannot slip past the
  // `/auth` guard, and respect the segment boundary so an unrelated route
  // that merely starts with the letters "auth" is not rejected.
  let decoded: string;
  try {
    decoded = decodeURIComponent(path);
  } catch {
    return undefined;
  }
  const auth = `/${APP_ROUTE.AUTH}`;
  if (decoded === auth || decoded.startsWith(`${auth}/`)) return undefined;
  if (decoded.startsWith('//') || decoded.startsWith('/\\')) return undefined;
  return path;
}

export function getAuthOnlyCallbackUrl(
  cookies: Record<string, unknown>,
  clientId?: string
) {
  let callbackUrl = `/${APP_ROUTE.HOME}`;

  const isAddonRoute = window.location.pathname.includes(`/${APP_ROUTE.ADDON}`);

  if (clientId && !isAddonRoute) {
    callbackUrl = `/ws/${clientId}/${APP_ROUTE.HOME}`;
  }

  return sanitizeCallbackUrl(cookies.callbackUrl) ?? callbackUrl;
}

type PostLoginSession = {
  user?: { onboarding?: string } | null;
  client?: { id?: string } | null;
  clients?: { id: string | number }[] | null;
  business?: unknown;
};

/**
 * Where an authenticated visitor of an anonymous-only route (e.g.
 * `/auth/login`) should be sent, or `null` when there is nowhere to go.
 *
 * This used to fall through to a `<Loader />` when the session resolved
 * without a `client.id`, stranding logged-in users on the login page (RCA
 * 2026-08-29). A `null` result means "onboarding is complete but the account
 * has no workspace to land on" — the caller must render an explicit message
 * (with a way out) rather than a spinner. No live route exists for that
 * state: `/onboarding` bounces completed users and every app route is
 * `/ws/:clientId/*`.
 */
export function resolvePostLoginPath(
  session: PostLoginSession | undefined,
  cookies: Record<string, unknown>
): string | null {
  const { user, client, clients } = session ?? {};

  if (user?.onboarding !== ONBOARDING.COMPLETE_STEP) {
    return `/${APP_ROUTE.ONBOARDING}`;
  }

  // The session query already falls back to the default client and returns
  // it as `client`; recomputing here only guards older cached shapes. Copy
  // first: the helper sorts in place and `clients` belongs to the cache.
  const clientId =
    client?.id ?? sortClientsAscendingByDate([...(clients ?? [])])[0]?.id;
  if (clientId) {
    return getAuthOnlyCallbackUrl(cookies, String(clientId));
  }

  return null;
}
